import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { PaginatedResponse } from '../../core/models/paginated-response.model';
import { User, UserFilters } from './user.model';
import { environment } from '@env';

/** Payload envoyé à l'API AdminUpdate */
interface UserUpdatePayload {
  idCredential: string;
  lastName?: string | null;
  firstName?: string | null;
  loginAd?: string | null;
  email?: string | null;
  idRemplacant?: string | null;
  idFiliale?: string | null;
  idMarque?: string | null;
  modeTransportBusCar?: boolean | null;
  modeTransportTram?: boolean | null;
  actif?: boolean | null;
  perimetresGeographiques?: string[];
  dorms?: string[];
}

/** Données du formulaire — les IDs sont directement les strings du form */
export type UserUpdateFormData = Omit<UserUpdatePayload, 'idCredential'>;

@Injectable({ providedIn: 'root' })
export class UsersService {
  private http = inject(HttpClient);

  private readonly baseUrl = `${environment.apiConfig.uri}/ParametrageUtilisateur`;
  private readonly url = `${this.baseUrl}/AdminGet`;

  getUserById(id: string): Observable<User> {
    return this.http.get<User>(`${this.baseUrl}/AdminGet/${id}`);
  }

  getUsers(filters?: UserFilters): Observable<PaginatedResponse<User>> {
    let params = new HttpParams();

    if (filters?.lastName) params = params.set('LastName', filters.lastName);
    if (filters?.firstName) params = params.set('FirstName', filters.firstName);
    if (filters?.loginAd) params = params.set('LoginAd', filters.loginAd);
    if (filters?.email) params = params.set('Email', filters.email);
    if (filters?.tri) params = params.set('Tri', filters.tri);
    if (filters?.actif != null) params = params.set('Actif', filters.actif);
    if (filters?.nbElem != null) params = params.set('Pagination.NbElem', filters.nbElem);
    if (filters?.numPage != null) params = params.set('Pagination.NumPage', filters.numPage);

    return this.http.get<PaginatedResponse<User>>(this.url, { params });
  }

  updateUser(id: string, payload: UserUpdateFormData): Observable<User> {
    const apiPayload: UserUpdatePayload = { ...payload, idCredential: id };
    return this.http.put<User>(`${this.baseUrl}`, apiPayload);
  }

  createUser(payload: UserUpdateFormData): Observable<User> {
    return this.http.post<User>(`${this.baseUrl}`, payload);
  }

  async toggleActif(id: string, actif: boolean): Promise<void> {
    if (actif) {
      await firstValueFrom(this.http.put<void>(`${this.baseUrl}/${id}/reactivate`, null));
    } else {
      await firstValueFrom(this.http.delete<void>(`${this.baseUrl}/delete/${id}`));
    }
  }
}
